/**
 * Notificaciones
 * -----------------------------------------------------------------------------
 * Avisos apilados en una esquina de la pantalla.
 *
 *   CompanyTheme.notify.success('Lote guardado');
 *   CompanyTheme.notify.error('No se pudo guardar', { detail: err.message });
 *   const aviso = CompanyTheme.notify.info('Procesando…', { delay: 0 });
 *   aviso.close();
 *
 * Qué se corrigió respecto de `showMDBAlert` (agro), de donde viene la idea:
 *
 *   · El elemento SE QUITA del DOM al cerrarse. Allí se hacía `appendChild` y se
 *     confiaba en el autohide de MDB, que solo lo oculta: cada aviso dejaba un
 *     `<div>` huérfano para el resto de la sesión.
 *   · El ancho lo pone el CSS. Allí se leía `window.innerWidth` al crearlo, así
 *     que un aviso abierto antes de rotar el móvil se quedaba con el ancho viejo.
 *   · La duración es la que se pide. Allí se subía a 6 s por dentro cuando no
 *     había recarga, sin avisar.
 *   · Se pausa al pasar el ratón o al enfocar: un error de 4 s no desaparece
 *     mientras lo estás leyendo.
 *   · Hay un máximo en pantalla: una ráfaga de errores ya no tapa la interfaz.
 *   · No recarga la página. Allí un `setTimeout` con `location.reload()` vivía
 *     dentro del helper y seguía adelante aunque cerraras el aviso a mano; eso
 *     es decisión de la aplicación, no del componente.
 */

import { notifyES } from './locale-es.js';

const DEFAULTS = {
  type: 'info',
  delay: 5000,
  position: 'top-right',
  max: 4,
  dismissible: true,
};

const ICONS = {
  success: 'fa-circle-check',
  error: 'fa-circle-xmark',
  warning: 'fa-triangle-exclamation',
  info: 'fa-circle-info',
};

// Los errores y avisos interrumpen; el resto se anuncia sin cortar al lector.
const ASSERTIVE = ['error', 'warning'];

const containers = new Map();

function containerFor(position) {
  if (containers.has(position)) return containers.get(position);

  const element = document.createElement('div');
  element.className = `owari-notifications owari-notifications-${position}`;
  element.setAttribute('aria-live', 'polite');
  element.setAttribute('aria-relevant', 'additions');
  document.body.appendChild(element);

  containers.set(position, element);
  return element;
}

function textNode(text) {
  const node = document.createElement('span');
  node.textContent = text;
  return node;
}

class Notification {
  constructor(options) {
    this.options = { ...DEFAULTS, ...options };
    this.container = containerFor(this.options.position);
    this.remaining = this.options.delay;
    this.timer = null;
    this.closed = false;

    this._build();
    this._enforceMax();
    this._start();
  }

  _build() {
    const { type, message, detail, dismissible } = this.options;

    this.element = document.createElement('div');
    this.element.className = `owari-notification owari-notification-${type}`;
    this.element.setAttribute('role', ASSERTIVE.indexOf(type) > -1 ? 'alert' : 'status');

    const icon = document.createElement('i');
    icon.className = `fas ${ICONS[type] || ICONS.info} owari-notification-icon`;
    icon.setAttribute('aria-hidden', 'true');

    const body = document.createElement('div');
    body.className = 'owari-notification-body';
    body.appendChild(textNode(message));

    if (detail) {
      const extra = document.createElement('p');
      extra.className = 'owari-notification-detail';
      extra.textContent = detail;
      body.appendChild(extra);
    }

    this.element.appendChild(icon);
    this.element.appendChild(body);

    if (dismissible) {
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'owari-notification-close';
      close.setAttribute('aria-label', notifyES.close);
      close.innerHTML = '<i class="fas fa-xmark" aria-hidden="true"></i>';
      close.addEventListener('click', () => this.close());
      this.element.appendChild(close);
    }

    // El temporizador se detiene mientras se lee o se navega con el teclado.
    this.element.addEventListener('mouseenter', () => this._pause());
    this.element.addEventListener('mouseleave', () => this._resume());
    this.element.addEventListener('focusin', () => this._pause());
    this.element.addEventListener('focusout', () => this._resume());

    this.container.appendChild(this.element);
  }

  /** Deja sitio cerrando los más antiguos cuando se supera el máximo. */
  _enforceMax() {
    const { max } = this.options;
    if (!max) return;

    const all = this.container.querySelectorAll('.owari-notification');
    for (let i = 0; i < all.length - max; i += 1) {
      const instance = all[i].__companyNotification;
      if (instance) instance.close();
      else all[i].remove();
    }
  }

  _start() {
    this.element.__companyNotification = this;
    if (!this.options.delay) return; // 0 = queda hasta que se cierre a mano

    this.startedAt = Date.now();
    this.timer = setTimeout(() => this.close(), this.remaining);
  }

  _pause() {
    if (!this.timer) return;

    clearTimeout(this.timer);
    this.timer = null;
    this.remaining -= Date.now() - this.startedAt;
    this.element.classList.add('is-paused');
  }

  _resume() {
    if (this.timer || this.closed || !this.options.delay) return;

    this.element.classList.remove('is-paused');
    this.startedAt = Date.now();
    this.timer = setTimeout(() => this.close(), Math.max(this.remaining, 500));
  }

  close() {
    if (this.closed) return;
    this.closed = true;

    clearTimeout(this.timer);
    this.element.classList.add('is-closing');

    // Se espera a la animación para quitarlo, pero con red de seguridad: si la
    // pestaña está en segundo plano el evento `transitionend` no llega nunca y
    // el nodo se quedaría en el DOM, que es justo lo que se quería evitar.
    const remove = () => {
      if (!this.element.parentNode) return;
      this.element.remove();
      if (typeof this.options.onClose === 'function') this.options.onClose();
    };

    this.element.addEventListener('transitionend', remove, { once: true });
    setTimeout(remove, 400);
  }
}

/**
 * Muestra una notificación.
 *
 * @param {Object|string} options Mensaje, u objeto de opciones.
 * @param {string} options.message  Texto principal.
 * @param {string} [options.detail] Segunda línea: detalle técnico, código…
 * @param {string} [options.type]   success | error | warning | info
 * @param {number} [options.delay]  Milisegundos; 0 la deja fija.
 * @param {string} [options.position] top-right | top-center | bottom-right | bottom-center
 * @param {number} [options.max]    Máximo en pantalla a la vez.
 * @returns {Notification}
 */
export function notify(options) {
  const config = typeof options === 'string' ? { message: options } : options || {};
  return new Notification(config);
}

['success', 'error', 'warning', 'info'].forEach((type) => {
  notify[type] = (message, options = {}) => notify({ ...options, type, message });
});

/** Cierra todas las notificaciones visibles. */
notify.clear = () => {
  containers.forEach((container) => {
    container.querySelectorAll('.owari-notification').forEach((element) => {
      const instance = element.__companyNotification;
      if (instance) instance.close();
      else element.remove();
    });
  });
};

export { Notification };
export default notify;
